import dagre from '@dagrejs/dagre'
import type { Node, Edge } from '@xyflow/react'

// ─── Types ───

export interface TrackerDocument {
  id: string
  slug: string
  folder: string | null
  title: string
  shortTitle: string
  seriesGroup: string
  status: string
  lastUpdated: string | null
  pipeline: Record<string, { done: boolean; date: string | null }>
  tags: string[]
  summary?: string
}

export interface Interrelation {
  source: string
  target: string
  type: string
  description: string
  bidirectional: boolean
}

export interface TrackerData {
  documents: TrackerDocument[]
  interrelations: Interrelation[]
}

export interface Concept {
  id: string
  label: string
  category: 'framework' | 'mechanism' | 'principle' | 'control' | 'pattern' | 'artifact'
  description: string
  importance: 'core' | 'supporting' | 'peripheral'
  section: string
}

export interface DocumentConcepts {
  documentId: string
  extractedAt: string
  concepts: Concept[]
}

export interface CrossReference {
  conceptId: string
  sourceDocId: string
  targetDocId: string
  strength: 'direct' | 'indirect'
}

export interface MindMapData {
  meta: { generatedAt: string; version: string; totalDocuments: number; totalConcepts: number }
  documents: Record<string, DocumentConcepts>
  crossReferences: CrossReference[]
}

// ─── Constants ───

export const GROUP_LABELS: Record<string, string> = {
  'core-14': 'Core 14',
  harness: 'Harness Theory',
  'stable-orbit-security': 'Stable Orbit Security',
  commercial: 'Commercial',
  'standalone-wp': 'Standalone',
  patterns: 'Patterns',
  '3bp-flagship': '3BP Flagship',
}

export const STATUS_COLORS: Record<string, string> = {
  final: '#22c55e',
  review: '#3b82f6',
  council_reviewed: '#3b82f6',
  partial: '#f59e0b',
  outlined: '#8b5cf6',
  'white-paper-only': '#a855f7',
  backlog: '#9ca3af',
  hold: '#ef4444',
}

export const EDGE_COLORS: Record<string, string> = {
  extends: '#3b82f6',
  feeds: '#f97316',
  unifies: '#a855f7',
  'pairs-with': '#22c55e',
  'depends-on': '#ef4444',
  implements: '#14b8a6',
  supports: '#9ca3af',
  references: '#6b7280',
}

export const CONCEPT_CATEGORY_COLORS: Record<string, string> = {
  framework: '#8b5cf6',
  mechanism: '#3b82f6',
  principle: '#22c55e',
  control: '#ef4444',
  pattern: '#f59e0b',
  artifact: '#06b6d4',
}

// ─── Node Dimensions ───

const GROUP_WIDTH = 200
const GROUP_HEIGHT = 60
const DOC_WIDTH = 180
const DOC_HEIGHT = 50
const CONCEPT_WIDTH = 160
const CONCEPT_HEIGHT = 36

// ─── Layout Computation ───

export function computeMindMapLayout(
  tracker: TrackerData | null,
  mindmap: MindMapData | null,
  expandedGroups: Set<string>,
  expandedDocs: Set<string>,
  searchFilter: string,
  hoveredNodeId: string | null,
): { nodes: Node[]; edges: Edge[] } {
  if (!tracker) return { nodes: [], edges: [] }

  const g = new dagre.graphlib.Graph()
  g.setGraph({
    rankdir: 'LR',
    nodesep: 40,
    ranksep: 100,
    marginx: 40,
    marginy: 40,
  })
  g.setDefaultEdgeLabel(() => ({}))

  const nodes: Node[] = []
  const edges: Edge[] = []

  // Group documents by seriesGroup
  const groups = new Map<string, TrackerDocument[]>()
  for (const doc of tracker.documents) {
    const group = doc.seriesGroup || 'standalone-wp'
    if (!groups.has(group)) groups.set(group, [])
    groups.get(group)!.push(doc)
  }

  // Determine which nodes are connected to the hovered node
  const hoveredConnections = new Set<string>()
  if (hoveredNodeId) {
    hoveredConnections.add(hoveredNodeId)
    for (const rel of tracker.interrelations) {
      if (rel.source === hoveredNodeId) hoveredConnections.add(rel.target)
      if (rel.target === hoveredNodeId) hoveredConnections.add(rel.source)
    }
    if (mindmap) {
      for (const xref of mindmap.crossReferences) {
        if (xref.conceptId === hoveredNodeId || xref.sourceDocId === hoveredNodeId) {
          hoveredConnections.add(xref.targetDocId)
          hoveredConnections.add(xref.conceptId)
        }
        if (xref.targetDocId === hoveredNodeId) {
          hoveredConnections.add(xref.sourceDocId)
          hoveredConnections.add(xref.conceptId)
        }
      }
    }
  }

  const searchLower = searchFilter.toLowerCase()
  const visibleDocIds = new Set<string>()

  // Build nodes for each group
  for (const [groupId, docs] of groups) {
    const groupNodeId = `group-${groupId}`
    const isExpanded = expandedGroups.has(groupId)
    const completedCount = docs.filter((d) => d.status === 'final' || d.status === 'review').length
    const conceptCount = docs.reduce((sum, d) => sum + (mindmap?.documents[d.id]?.concepts.length || 0), 0)

    // Filter by search if active
    const matchesSearch = !searchFilter || GROUP_LABELS[groupId]?.toLowerCase().includes(searchLower)
    const hasMatchingDoc = !searchFilter || docs.some(
      (d) =>
        d.shortTitle.toLowerCase().includes(searchLower) ||
        d.title.toLowerCase().includes(searchLower) ||
        d.id.toLowerCase().includes(searchLower),
    )

    if (searchFilter && !matchesSearch && !hasMatchingDoc) continue

    const dimmed = hoveredNodeId ? !hoveredConnections.has(groupNodeId) : false

    g.setNode(groupNodeId, { width: GROUP_WIDTH, height: GROUP_HEIGHT })
    nodes.push({
      id: groupNodeId,
      type: 'mindmapGroup',
      position: { x: 0, y: 0 },
      data: {
        label: GROUP_LABELS[groupId] || groupId,
        docCount: docs.length,
        completedCount,
        conceptCount,
        expanded: isExpanded,
        groupId,
        dimmed,
      },
    })

    if (isExpanded) {
      for (const doc of docs) {
        // Filter by search
        if (
          searchFilter &&
          !doc.shortTitle.toLowerCase().includes(searchLower) &&
          !doc.title.toLowerCase().includes(searchLower) &&
          !doc.id.toLowerCase().includes(searchLower)
        ) {
          continue
        }

        visibleDocIds.add(doc.id)
        const docNodeId = `doc-${doc.id}`
        const isDocExpanded = expandedDocs.has(doc.id)
        const docConcepts = mindmap?.documents[doc.id]?.concepts || []
        const docDimmed = hoveredNodeId ? !hoveredConnections.has(doc.id) && !hoveredConnections.has(docNodeId) : false

        g.setNode(docNodeId, { width: DOC_WIDTH, height: DOC_HEIGHT })
        nodes.push({
          id: docNodeId,
          type: 'mindmapDoc',
          position: { x: 0, y: 0 },
          data: {
            docId: doc.id,
            label: doc.shortTitle,
            fullTitle: doc.title,
            status: doc.status,
            statusColor: STATUS_COLORS[doc.status] || '#9ca3af',
            conceptCount: docConcepts.length,
            expanded: isDocExpanded,
            summary: doc.summary,
            dimmed: docDimmed,
          },
        })

        // Edge from group to doc
        g.setEdge(groupNodeId, docNodeId)
        edges.push({
          id: `e-${groupNodeId}-${docNodeId}`,
          source: groupNodeId,
          target: docNodeId,
          type: 'mindmapEdge',
          data: { edgeType: 'hierarchy', dimmed: docDimmed },
          style: { stroke: '#d1d5db', strokeWidth: 1 },
        })

        // Expand concepts
        if (isDocExpanded && docConcepts.length > 0) {
          for (const concept of docConcepts) {
            const conceptNodeId = `concept-${concept.id}`
            const conceptDimmed = hoveredNodeId ? !hoveredConnections.has(concept.id) && !hoveredConnections.has(conceptNodeId) : false

            g.setNode(conceptNodeId, { width: CONCEPT_WIDTH, height: CONCEPT_HEIGHT })
            nodes.push({
              id: conceptNodeId,
              type: 'mindmapConcept',
              position: { x: 0, y: 0 },
              data: {
                conceptId: concept.id,
                label: concept.label,
                category: concept.category,
                categoryColor: CONCEPT_CATEGORY_COLORS[concept.category] || '#6b7280',
                importance: concept.importance,
                description: concept.description,
                section: concept.section,
                dimmed: conceptDimmed,
              },
            })

            // Edge from doc to concept
            g.setEdge(docNodeId, conceptNodeId)
            edges.push({
              id: `e-${docNodeId}-${conceptNodeId}`,
              source: docNodeId,
              target: conceptNodeId,
              type: 'mindmapEdge',
              data: { edgeType: 'hierarchy', dimmed: conceptDimmed },
              style: { stroke: CONCEPT_CATEGORY_COLORS[concept.category] || '#6b7280', strokeWidth: 1, opacity: 0.5 },
            })
          }
        }
      }
    }
  }

  // Add interrelation edges between visible documents
  for (const rel of tracker.interrelations) {
    const sourceNodeId = `doc-${rel.source}`
    const targetNodeId = `doc-${rel.target}`
    if (!visibleDocIds.has(rel.source) || !visibleDocIds.has(rel.target)) continue

    const dimmed = hoveredNodeId
      ? !hoveredConnections.has(rel.source) && !hoveredConnections.has(rel.target)
      : false

    edges.push({
      id: `rel-${rel.source}-${rel.target}-${rel.type}`,
      source: sourceNodeId,
      target: targetNodeId,
      type: 'mindmapEdge',
      data: {
        edgeType: rel.type,
        description: rel.description,
        bidirectional: rel.bidirectional,
        dimmed,
      },
      style: {
        stroke: EDGE_COLORS[rel.type] || '#6b7280',
        strokeWidth: 2,
      },
      animated: rel.type === 'feeds',
    })
  }

  // Add cross-reference edges between visible concepts
  if (mindmap) {
    for (const xref of mindmap.crossReferences) {
      const sourceConceptNodeId = `concept-${xref.conceptId}`
      const targetDocNodeId = `doc-${xref.targetDocId}`

      // Only show if both endpoints are visible
      const sourceVisible = nodes.some((n) => n.id === sourceConceptNodeId)
      const targetVisible = nodes.some((n) => n.id === targetDocNodeId)
      if (!sourceVisible || !targetVisible) continue

      const dimmed = hoveredNodeId
        ? !hoveredConnections.has(xref.conceptId) && !hoveredConnections.has(xref.targetDocId)
        : false

      edges.push({
        id: `xref-${xref.conceptId}-${xref.targetDocId}`,
        source: sourceConceptNodeId,
        target: targetDocNodeId,
        type: 'mindmapEdge',
        data: {
          edgeType: xref.strength === 'direct' ? 'xref-direct' : 'xref-indirect',
          dimmed,
        },
        style: {
          stroke: xref.strength === 'direct' ? '#6366f1' : '#a5b4fc',
          strokeWidth: 1,
          strokeDasharray: xref.strength === 'direct' ? '5 3' : '3 5',
        },
      })
    }
  }

  // Run dagre layout
  dagre.layout(g)

  // Apply dagre positions to nodes
  for (const node of nodes) {
    const dagreNode = g.node(node.id)
    if (dagreNode) {
      node.position = {
        x: dagreNode.x - (dagreNode.width || 0) / 2,
        y: dagreNode.y - (dagreNode.height || 0) / 2,
      }
    }
  }

  return { nodes, edges }
}
